import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, KeyRound, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { D1Logo } from "@/components/D1Logo";

export default function ChangePassword() {
  const { user, changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    if (next.length < 6) {
      toast.error("A nova senha deve ter ao menos 6 caracteres");
      return;
    }
    if (next !== confirm) {
      toast.error("As senhas não conferem");
      return;
    }
    setLoading(true);
    try {
      await changePassword(current, next);
      toast.success("Senha atualizada com sucesso");
      navigate(user.role === "admin" ? "/admin" : "/motorista", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen w-full bg-slate-50 p-6">
      <div className="mx-auto flex max-w-lg flex-col gap-6 pt-10">
        <D1Logo variant="light" />
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
            Primeira utilização
          </p>
          <h1 className="mt-2 font-display text-3xl font-bold text-slate-900">Trocar senha</h1>
          <p className="mt-1 text-sm text-slate-500">
            Antes de continuar, defina uma nova senha de acesso pessoal.
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="current" className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Senha atual
              </Label>
              <Input
                id="current"
                data-testid="cp-current-input"
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className="h-12"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="next" className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Nova senha
              </Label>
              <Input
                id="next"
                data-testid="cp-new-input"
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                className="h-12"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm" className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Confirmar nova senha
              </Label>
              <Input
                id="confirm"
                data-testid="cp-confirm-input"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="h-12"
              />
            </div>
            <Button
              type="submit"
              data-testid="cp-submit-button"
              disabled={loading}
              className="h-12 w-full bg-blue-900 text-base font-semibold hover:bg-blue-800"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <span className="inline-flex items-center gap-2"><KeyRound className="h-4 w-4" /> Atualizar senha</span>
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => { logout(); navigate("/login", { replace: true }); }}
              className="h-10 w-full text-slate-500"
              data-testid="cp-logout-button"
            >
              <LogOut className="mr-2 h-4 w-4" /> Sair
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
